import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, SEVERITIES, STOCK, INV_ICON } from "../lib/categories";
import { PushButton } from "../components/PushButton";
import { MediaInput } from "../components/MediaInput";
import { VoiceRecorderInput } from "../components/VoiceRecorderInput";
import { AdminQA } from "../components/AdminQA";
import { SearchableSelect } from "../components/SearchableSelect";
import { AdminUsers, AdminClinics, AdminCategories, AdminBanners, AdminSettings } from "../components/AdminConfig";
import { AccountArea } from "../components/AccountArea";
import { AdminAnalytics } from "../components/AdminAnalytics";
import { AdminAudit } from "../components/AdminAudit";
import { AdminAppointments, LiveConsultations } from "../components/AdminCare";
import { AdminBroadcast } from "../components/AdminBroadcast";
import { AuditMeta } from "../components/AuditMeta";
import { AdminTestRoles } from "../components/AdminTestRoles";
import { AdminClinicianDirectory } from "../components/AdminClinicianDirectory";
import { AdminRequests, AdminMessages, AdminTraining, AdminEnquiries, AdminGroupCalls } from "../components/AdminExtras";
import { PatientRequests } from "../components/PatientExtras";
import { Footer } from "../components/Footer";
import { useAppData } from "../context/AppDataContext";
import { toast } from "sonner";
import {
  HeartPulse, LayoutDashboard, Megaphone, Package, Clock, Calendar,
  Plus, Pencil, Trash2, X, Sparkles, LogOut, AlertTriangle, ExternalLink, Bell, MessageSquare,
  Users, Building2, Tags, Image as ImageIconL, Settings as SettingsIcon, BarChart3, ScrollText,
  Stethoscope, Video, FlaskConical, CalendarCheck, GraduationCap, Inbox, Pin, PinOff, GripVertical, Search, UserCog, MapPin,
} from "lucide-react";

const CONTENT_TABS = [
  { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { k: "announcements", label: "Announcements", icon: Megaphone },
  { k: "inventory", label: "Inventory", icon: Package },
  { k: "schedules", label: "Schedules", icon: Clock },
  { k: "events", label: "Events", icon: Calendar },
  { k: "appointments", label: "Appointments", icon: Video },
  { k: "requests", label: "Requests", icon: CalendarCheck },
  { k: "qa", label: "Events Q&A", icon: MessageSquare },
  { k: "messages", label: "Inbox", icon: MessageSquare },
  { k: "training", label: "Training", icon: GraduationCap },
  { k: "analytics", label: "Analytics", icon: BarChart3 },
];
const MGMT_TABS = [
  { k: "enquiries", label: "General Enquiries", icon: Inbox },
  { k: "users", label: "Users", icon: Users },
  { k: "clinics", label: "Clinics", icon: Building2 },
  { k: "categories", label: "Categories", icon: Tags },
  { k: "banners", label: "Banners", icon: ImageIconL },
  { k: "audit", label: "Audit Log", icon: ScrollText },
  { k: "settings", label: "Settings", icon: SettingsIcon },
];

function ClinicMulti({ clinics, value = [], onChange }) {
  const active = clinics.filter((c) => c.active);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div data-testid="clinic-targets" className="flex flex-wrap gap-2">
      {active.length === 0 && <span className="text-sm text-muted2">No clinics yet.</span>}
      {active.map((c) => (
        <button type="button" key={c.id} onClick={() => toggle(c.id)} className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${value.includes(c.id) ? "bg-clinical text-white border-clinical" : "bg-white border-line text-muted2"}`}>
          {c.name}
        </button>
      ))}
      <p className="basis-full text-xs text-muted2">Leave none selected to broadcast to all clinics.</p>
    </div>
  );
}

function ClinicOne({ clinics, value, onChange, testid }) {
  const active = clinics.filter((c) => c.active);
  return (
    <SearchableSelect
      testid={testid}
      options={active.map((c) => ({ value: c.id, label: c.name }))}
      value={value}
      onChange={onChange}
      placeholder="— Select clinic —"
      searchPlaceholder="Search clinics…"
      clearable
    />
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-bold text-muted2">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
const inputCls =
  "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="form-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl text-ink">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn"><X className="w-5 h-5 text-muted2" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { clinics, catsByKind } = useAppData();
  const isManager = user?.role === "system_admin" || user?.role === "super_admin";
  const isSystemAdmin = user?.role === "system_admin";
  const canClinicians = isManager || user?.role === "admin";
  const TABS = [...CONTENT_TABS, ...(canClinicians ? [{ k: "clinicians", label: "Clinicians", icon: Stethoscope }] : []), ...(isManager ? MGMT_TABS : []), ...(isSystemAdmin ? [{ k: "testroles", label: "Test Roles", icon: FlaskConical }] : []), { k: "account", label: "My Account", icon: UserCog }];
  const [tab, setTab] = useState("dashboard");
  const [reqRefresh, setReqRefresh] = useState(0);
  const [stats, setStats] = useState(null);
  const [lists, setLists] = useState({ announcements: [], inventory: [], schedules: [], events: [] });
  const [modal, setModal] = useState(null); // {type, item}
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [waitingCount, setWaitingCount] = useState(0);
  const [enqUnread, setEnqUnread] = useState(0);
  const [initialEnquiry, setInitialEnquiry] = useState(null);
  const [apptPrefill, setApptPrefill] = useState(null);
  const [listSearch, setListSearch] = useState("");
  const [listSort, setListSort] = useState("created_desc");
  useEffect(() => { setListSearch(""); }, [tab]);

  const clinicNameById = (id) => clinics.find((c) => c.id === id)?.name || "";
  const visibleList = (() => {
    const rows = lists[tab] || [];
    const q = listSearch.trim().toLowerCase();
    let out = q
      ? rows.filter((it) => [it.title, it.name, it.body, it.category, it.location, it.created_by,
          clinicNameById(it.clinic_id), ...(it.clinic_ids || []).map(clinicNameById)]
          .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
      : [...rows];
    const key = listSort.replace(/_asc$|_desc$/, "");
    const dir = listSort.endsWith("_asc") ? 1 : -1;
    const val = (it) => {
      if (key === "clinic") return (clinicNameById(it.clinic_id) || (it.clinic_ids || []).map(clinicNameById)[0] || it.clinic || "").toLowerCase();
      if (key === "author") return (it.created_by || "").toLowerCase();
      if (key === "category") return (it.category || "").toLowerCase();
      return String(it.created_at || "");
    };
    out.sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
    // Keep pinned announcements on top regardless of sort.
    if (tab === "announcements") out.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    return out;
  })();
  const scheduleFromRequest = useCallback((req) => {
    setApptPrefill({
      public_code: req.patient_code || "",
      specialist_id: req.preferred_specialist_id || "",
      request_id: req.id,
      _ts: Date.now(),
    });
    setTab("appointments");
  }, []);
  const bookForDay = useCallback((date) => {
    setApptPrefill({ date, _ts: Date.now() });
    setTab("appointments");
  }, []);
  useEffect(() => {
    if (!isManager) return;
    const gid = new URLSearchParams(window.location.search).get("enquiry");
    if (gid) {
      setTab("enquiries");
      setInitialEnquiry(gid);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [isManager]);
  const loadEnqUnread = useCallback(async () => {
    if (!isManager) return;
    try { const { data } = await api.get("/admin/enquiries-unread"); setEnqUnread(data.unread || 0); } catch (e) { console.debug("enquiries-unread poll failed:", e); }
  }, [isManager]);
  useEffect(() => {
    loadEnqUnread();
    const t = setInterval(loadEnqUnread, 20000);
    return () => clearInterval(t);
  }, [loadEnqUnread]);
  useEffect(() => { if (tab === "enquiries") setEnqUnread(0); }, [tab]);

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/appointments");
        const n = data.filter((a) => a.patient_waiting && a.status === "scheduled").length;
        if (active) setWaitingCount(n);
      } catch (e) { console.debug("waiting-count poll failed:", e); }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    document.title = waitingCount > 0
      ? `(${waitingCount}) Patient${waitingCount > 1 ? "s" : ""} waiting · Hutanohwevanhu.org`
      : "Hutanohwevanhu.org Admin";
    return () => { document.title = "Hutanohwevanhu.org Admin"; };
  }, [waitingCount]);

  const load = useCallback(async () => {
    const [a, i, s, e, st] = await Promise.all([
      api.get("/announcements"), api.get("/inventory"),
      api.get("/schedules"), api.get("/events"), api.get("/stats"),
    ]);
    setLists({ announcements: a.data, inventory: i.data, schedules: s.data, events: e.data });
    setStats(st.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (type, item = null) => {
    const defaults = {
      announcements: { title: "", body: "", category: "general", severity: "info", is_emergency: false, expires_at: "", send_push: false, audio_url: "", media: [], clinic_ids: [], whatsapp_enabled: false },
      inventory: { name: "", type: "medication", quantity: 0, unit: "units", threshold: 5, location: "", clinic_id: "" },
      schedules: { title: "", schedule_type: "recurring", day: "", date: "", time: "", clinic: "", description: "", clinic_id: "" },
      events: { title: "", description: "", date: "", location: "", category: "general", audio_url: "", media: [], clinic_ids: [] },
    };
    setForm(item ? { ...item } : defaults[type]);
    setModal({ type, item });
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    const { type, item } = modal;
    const payload = { ...form };
    delete payload.id; delete payload.status; delete payload.created_by; delete payload.created_at; delete payload.updated_at;
    if (type === "inventory") { payload.quantity = Number(payload.quantity); payload.threshold = Number(payload.threshold); }
    try {
      let resp;
      if (item) resp = await api.put(`/${type}/${item.id}`, payload);
      else resp = await api.post(`/${type}`, payload);
      const pushed = resp?.data?.push_sent;
      if (typeof pushed === "number") toast.success(`Saved · push sent to ${pushed} subscriber${pushed === 1 ? "" : "s"}`);
      else toast.success(item ? "Updated" : "Created");
      setModal(null);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const remove = async (type, id) => {
    try { await api.delete(`/${type}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const togglePin = async (id) => {
    try { const { data } = await api.put(`/announcements/${id}/pin`); toast.success(data.pinned ? "Pinned to top" : "Unpinned"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const dragId = useRef(null);
  const reorderPins = async (targetId) => {
    const pinned = lists.announcements.filter((a) => a.pinned).map((a) => a.id);
    const from = pinned.indexOf(dragId.current);
    const to = pinned.indexOf(targetId);
    dragId.current = null;
    if (from < 0 || to < 0 || from === to) return;
    pinned.splice(to, 0, pinned.splice(from, 1)[0]);
    try { await api.put("/announcements/pins/reorder", { ordered_ids: pinned }); toast.success("Pin order updated"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const aiGenerate = async (mode) => {
    if (mode === "draft" && !form.title?.trim()) {
      toast.error("Enter a title/topic first so AI can draft the content.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate", {
        mode, topic: form.title, category: form.category, text: form.body,
      });
      if (mode === "summarize") upd("body", data.result);
      else upd("body", data.result);
      toast.success("AI draft ready");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      <header className="bg-clinical text-white" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo-link" className="flex items-center gap-2 font-heading font-black text-xl tracking-tight hover:opacity-90 transition-opacity">
            <HeartPulse className="w-6 h-6 text-alert" /> Hutanohwevanhu.org <span className="text-white/50 text-sm font-body font-normal">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <PushButton userType="staff" variant="dark" />
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white" data-testid="view-public-link">
              <ExternalLink className="w-4 h-4" /> View public site
            </Link>
            <span className="text-sm text-white/80 hidden sm:block">{user?.name}</span>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto w-full px-5 py-3 flex gap-2 overflow-x-auto border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.k ? "bg-clinical text-white" : "text-muted2 hover:bg-line/50"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            {t.k === "dashboard" && waitingCount > 0 && (
              <span data-testid={`waiting-count-badge-${t.k}`} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-critical text-white text-[10px] font-bold animate-pulse">{waitingCount}</span>
            )}
          </button>
        ))}
      </div>

      <main className="max-w-7xl mx-auto w-full px-5 py-8 flex-1">
        {tab === "dashboard" && stats && (
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6">Overview</h1>
            <LiveConsultations />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { label: "Announcements", val: stats.announcements, c: "#1F4E79" },
                { label: "Emergencies", val: stats.emergencies, c: "#E63946" },
                { label: "Inventory Items", val: stats.inventory_items, c: "#4A7C59" },
                { label: "Low / Out Stock", val: stats.low_stock, c: "#F4A261" },
                { label: "Schedules", val: stats.schedules, c: "#1F4E79" },
                { label: "Events", val: stats.events, c: "#4A7C59" },
                { label: "Pending Q&A", val: stats.pending_questions || 0, c: "#E63946" },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-line rounded-md p-5" data-testid="stat-card">
                  <p className="font-heading font-black text-4xl" style={{ color: s.c }}>{s.val}</p>
                  <p className="text-xs uppercase tracking-wider font-bold text-muted2 mt-2">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8" data-testid="per-clinic-overview">
              <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Building2 className="w-5 h-5 text-clinical" /> Per-clinic stock health check</h3>
              <p className="text-sm text-muted2 mt-1">Low / out-of-stock inventory grouped by clinic location. Patient appointments are private and are not shown here.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {(() => {
                  const activeClinics = clinics.filter((c) => c.active);
                  const unassigned = lists.inventory.filter((i) => !i.clinic_id);
                  const cards = [...activeClinics];
                  if (unassigned.length > 0) cards.push({ id: null, name: "Unassigned", active: true });
                  return cards.map((clinic) => {
                    const items = lists.inventory.filter((i) => (i.clinic_id || null) === (clinic.id || null));
                    const low = items.filter((i) => i.status !== "available");
                    return (
                      <div key={clinic.id || "unassigned"} data-testid="clinic-health-card" className={`bg-white border rounded-md p-4 ${low.length > 0 ? "border-l-4 border-critical" : "border-line"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-heading font-bold text-ink truncate">{clinic.name}</span>
                          {low.length > 0 ? (
                            <span className="text-xs font-bold bg-critical/10 text-critical rounded-full px-2 py-0.5 shrink-0">{low.length} low</span>
                          ) : (
                            <span className="text-xs font-bold bg-calm/10 text-calm rounded-full px-2 py-0.5 shrink-0">Healthy</span>
                          )}
                        </div>
                        {items.length === 0 ? (
                          <p className="text-xs text-muted2 mt-2">No inventory logged for this clinic yet.</p>
                        ) : low.length === 0 ? (
                          <p className="text-xs text-calm mt-2">All {items.length} item{items.length === 1 ? "" : "s"} in stock.</p>
                        ) : (
                          <div className="mt-2 grid gap-1">
                            {low.map((i) => (
                              <div key={i.id} className="flex items-center justify-between text-sm border border-line rounded px-2.5 py-1.5">
                                <span className="truncate">{i.name}</span>
                                <span className="font-bold shrink-0 ml-2" style={{ color: STOCK[i.status].color }}>{i.quantity} {i.unit} · {STOCK[i.status].label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {tab === "qa" && <AdminQA onChange={load} />}
        {tab === "requests" && (
          <div className="space-y-8">
            <PatientRequests onCreated={() => setReqRefresh((k) => k + 1)} />
            <AdminRequests key={reqRefresh} onSchedule={scheduleFromRequest} />
          </div>
        )}
        {tab === "messages" && <AdminMessages />}
        {tab === "enquiries" && <AdminEnquiries onChange={loadEnqUnread} initialGuestId={initialEnquiry} />}
        {tab === "training" && <AdminTraining />}
        {tab === "analytics" && <AdminAnalytics />}
        {tab === "audit" && <AdminAudit />}
        {tab === "appointments" && <><AdminAppointments prefill={apptPrefill} /><div className="mt-10"><AdminGroupCalls /></div></>}
        {tab === "users" && <AdminUsers me={user} onChange={load} />}
        {tab === "clinics" && <AdminClinics />}
        {tab === "categories" && <AdminCategories />}
        {tab === "banners" && <AdminBanners />}
        {tab === "settings" && <AdminSettings onChange={load} onBookDay={bookForDay} />}
        {tab === "testroles" && <AdminTestRoles />}
        {tab === "clinicians" && <AdminClinicianDirectory canEdit={isManager} />}
        {tab === "account" && <AccountArea />}

        {["announcements", "inventory", "schedules", "events"].includes(tab) && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading font-bold text-3xl tracking-tight text-ink capitalize">{tab}</h1>
              <div className="flex items-center gap-2">
                {tab === "announcements" && isManager && <AdminBroadcast />}
                <button onClick={() => openModal(tab)} data-testid="add-item-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-clinicaldark transition-colors">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="admin-list-search"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder={`Search ${tab}…`}
                  className="w-full border border-line bg-white rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-clinical"
                />
              </div>
              <select data-testid="admin-list-sort" value={listSort} onChange={(e) => setListSort(e.target.value)} className="border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical">
                <option value="created_desc">Date created (newest)</option>
                <option value="created_asc">Date created (oldest)</option>
                <option value="clinic_asc">Clinic (A–Z)</option>
                <option value="clinic_desc">Clinic (Z–A)</option>
                <option value="author_asc">Author (A–Z)</option>
                <option value="author_desc">Author (Z–A)</option>
                <option value="category_asc">Category (A–Z)</option>
                <option value="category_desc">Category (Z–A)</option>
              </select>
            </div>

            <div className="grid gap-3" data-testid="admin-list">
              {visibleList.length === 0 && (
                <p data-testid="admin-list-empty" className="text-sm text-muted2 py-6 text-center">No {tab} match your search.</p>
              )}
              {visibleList.map((it) => {
                const pinDrag = tab === "announcements" && it.pinned && !listSearch && listSort === "created_desc";
                return (
                <div
                  key={it.id}
                  data-testid="admin-row"
                  draggable={pinDrag}
                  onDragStart={pinDrag ? () => { dragId.current = it.id; } : undefined}
                  onDragOver={pinDrag ? (e) => e.preventDefault() : undefined}
                  onDrop={pinDrag ? () => reorderPins(it.id) : undefined}
                  className={`bg-white border border-line rounded-md p-4 flex items-start justify-between gap-4 ${pinDrag ? "cursor-move" : ""}`}
                >
                  <div className="min-w-0 flex items-start gap-2">
                    {pinDrag && <GripVertical data-testid="pin-drag-handle" className="w-4 h-4 text-muted2 mt-1 shrink-0" />}
                    <div className="min-w-0">
                    {tab === "announcements" && (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-bold text-ink">{it.title}</span>
                          {it.pinned && <span data-testid="ann-pinned-badge" className="text-xs font-bold text-clinical bg-clinical/10 px-2 py-0.5 rounded inline-flex items-center gap-1"><Pin className="w-3 h-3" /> PINNED</span>}
                          {it.is_emergency && <span className="text-xs font-bold text-critical bg-critical/10 px-2 py-0.5 rounded">EMERGENCY</span>}
                          {it.expires_at && <span data-testid="ann-expiry-badge" className={`text-xs font-semibold px-2 py-0.5 rounded ${it.expires_at < new Date().toISOString().slice(0, 10) ? "bg-muted2/20 text-muted2" : "bg-alert/10 text-alert"}`}>{it.expires_at < new Date().toISOString().slice(0, 10) ? "Expired" : `Expires ${it.expires_at}`}</span>}
                          <span className="text-xs text-muted2">{CATEGORIES[it.category]?.label}</span>
                        </div>
                        <p className="text-sm text-muted2 mt-1 line-clamp-2">{it.body}</p>
                      </>
                    )}
                    {tab === "inventory" && (
                      <div className="flex items-center gap-3">
                        {(() => { const I = INV_ICON[it.type] || Package; return <I className="w-5 h-5 text-clinical" />; })()}
                        <div>
                          <span className="font-heading font-bold text-ink">{it.name}</span>
                          <p className="text-sm text-muted2">{it.quantity} {it.unit} · threshold {it.threshold} · <span style={{ color: STOCK[it.status].color }} className="font-semibold">{STOCK[it.status].label}</span></p>
                          {(clinics.find((c) => c.id === it.clinic_id)?.name || it.location) && (
                            <p className="text-xs text-muted2 mt-0.5 inline-flex items-center gap-1.5" data-testid="inventory-clinic">
                              <MapPin className="w-3.5 h-3.5 text-clinical shrink-0" /> {clinics.find((c) => c.id === it.clinic_id)?.name || it.location}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {tab === "schedules" && (
                      <>
                        <span className="font-heading font-bold text-ink">{it.title}</span>
                        <p className="text-sm text-muted2 mt-1">{it.schedule_type === "dated" ? it.date : it.day} · {it.time} · {it.clinic}</p>
                      </>
                    )}
                    {tab === "events" && (
                      <>
                        <span className="font-heading font-bold text-ink">{it.title}</span>
                        <p className="text-sm text-muted2 mt-1">{new Date(it.date).toDateString()} · {it.location}</p>
                      </>
                    )}
                    <AuditMeta createdAt={it.created_at} createdBy={it.created_by} />
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {tab === "announcements" && (
                      <button onClick={() => togglePin(it.id)} data-testid="pin-btn" title={it.pinned ? "Unpin" : "Pin to top"} className={`p-2 rounded ${it.pinned ? "text-clinical bg-clinical/10" : "text-muted2 hover:bg-line/50"}`}>
                        {it.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      </button>
                    )}
                    <button onClick={() => openModal(tab, it)} data-testid="edit-btn" className="p-2 hover:bg-line/50 rounded text-clinical"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(tab, it.id)} data-testid="delete-btn" className="p-2 hover:bg-critical/10 rounded text-critical"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );})}
              {lists[tab].length === 0 && <p className="text-muted2">Nothing here yet. Click Add to create one.</p>}
            </div>
          </div>
        )}
      </main>

      <Footer />

      {/* Form Modal */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={`${modal?.item ? "Edit" : "New"} ${modal?.type?.slice(0, -1)}`}>
        {modal && (
          <div className="space-y-4">
            {modal.type === "announcements" && (
              <>
                <Field label="Title"><input className={inputCls} data-testid="f-title" value={form.title} onChange={(e) => upd("title", e.target.value)} /></Field>
                <Field label="Body">
                  <textarea rows={5} className={inputCls} data-testid="f-body" value={form.body} onChange={(e) => upd("body", e.target.value)} />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => aiGenerate("draft")} disabled={busy} data-testid="ai-draft-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-1.5 hover:bg-clinical/5 disabled:opacity-50">
                      <Sparkles className="w-3.5 h-3.5" /> AI Draft
                    </button>
                    <button onClick={() => aiGenerate("summarize")} disabled={busy || !form.body} data-testid="ai-summarize-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-calm border border-calm/30 rounded-md px-3 py-1.5 hover:bg-calm/5 disabled:opacity-50">
                      <Sparkles className="w-3.5 h-3.5" /> AI Summarize
                    </button>
                  </div>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Category">
                    <select className={inputCls} data-testid="f-category" value={form.category} onChange={(e) => upd("category", e.target.value)}>
                      {catsByKind("announcement").map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Severity">
                    <select className={inputCls} data-testid="f-severity" value={form.severity} onChange={(e) => upd("severity", e.target.value)}>
                      {Object.keys(SEVERITIES).map((k) => <option key={k} value={k}>{SEVERITIES[k].label}</option>)}
                    </select>
                  </Field>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="f-emergency" checked={!!form.is_emergency} onChange={(e) => upd("is_emergency", e.target.checked)} />
                  Mark as emergency (shows in critical banner)
                </label>
                <Field label="Auto-expiry date (optional)">
                  <div className="flex items-center gap-2">
                    <input type="date" data-testid="f-expires-at" className={inputCls} value={form.expires_at || ""} onChange={(e) => upd("expires_at", e.target.value)} />
                    {form.expires_at && <button type="button" data-testid="f-expires-clear" onClick={() => upd("expires_at", "")} className="text-sm font-semibold text-muted2 whitespace-nowrap">Clear</button>}
                  </div>
                  <p className="text-xs text-muted2 mt-1">After this date the alert automatically unpins and drops off the banner.</p>
                </Field>
                <label className="flex items-center gap-2 text-sm bg-alert/10 border border-alert/30 rounded-md px-3 py-2">
                  <input type="checkbox" data-testid="f-send-push" checked={!!form.send_push} onChange={(e) => upd("send_push", e.target.checked)} />
                  <Bell className="w-4 h-4 text-alert" /> Send push alert to all subscribers' phones
                </label>
                <label className="flex items-center gap-2 text-sm bg-[#25D366]/10 border border-[#25D366]/30 rounded-md px-3 py-2">
                  <input type="checkbox" data-testid="f-whatsapp" checked={!!form.whatsapp_enabled} onChange={(e) => upd("whatsapp_enabled", e.target.checked)} />
                  <MessageSquare className="w-4 h-4 text-[#25D366]" /> Show WhatsApp contact button on this announcement
                </label>
                <Field label="Target clinics">
                  <ClinicMulti clinics={clinics} value={form.clinic_ids || []} onChange={(v) => upd("clinic_ids", v)} />
                </Field>
                <Field label="Media (images / short videos / URLs)">
                  <MediaInput media={form.media || []} onChange={(m) => upd("media", m)} />
                </Field>
                <Field label="Voice note (optional)">
                  <VoiceRecorderInput value={form.audio_url || ""} onChange={(u) => upd("audio_url", u)} testid="announcement-voice" />
                </Field>

              </>
            )}
            {modal.type === "inventory" && (
              <>
                <Field label="Name"><input className={inputCls} data-testid="f-name" value={form.name} onChange={(e) => upd("name", e.target.value)} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Type">
                    <select className={inputCls} data-testid="f-type" value={form.type} onChange={(e) => upd("type", e.target.value)}>
                      {catsByKind("inventory").map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Clinic location"><ClinicOne clinics={clinics} value={form.clinic_id} onChange={(v) => upd("clinic_id", v)} testid="f-inv-clinic" /></Field>
                  <Field label="Location"><input className={inputCls} data-testid="f-location" value={form.location} onChange={(e) => upd("location", e.target.value)} /></Field>
                  <Field label="Quantity"><input type="number" className={inputCls} data-testid="f-quantity" value={form.quantity} onChange={(e) => upd("quantity", e.target.value)} /></Field>
                  <Field label="Unit"><input className={inputCls} data-testid="f-unit" value={form.unit} onChange={(e) => upd("unit", e.target.value)} /></Field>
                  <Field label="Low-stock threshold"><input type="number" className={inputCls} data-testid="f-threshold" value={form.threshold} onChange={(e) => upd("threshold", e.target.value)} /></Field>
                </div>
              </>
            )}
            {modal.type === "schedules" && (
              <>
                <Field label="Title"><input className={inputCls} data-testid="f-title" value={form.title} onChange={(e) => upd("title", e.target.value)} /></Field>
                <Field label="Schedule type">
                  <select className={inputCls} data-testid="f-sch-type" value={form.schedule_type || "recurring"} onChange={(e) => upd("schedule_type", e.target.value)}>
                    <option value="recurring">Recurring (day of week)</option>
                    <option value="dated">Specific date</option>
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  {form.schedule_type === "dated" ? (
                    <Field label="Date"><input type="date" className={inputCls} data-testid="f-sch-date" value={form.date || ""} onChange={(e) => upd("date", e.target.value)} /></Field>
                  ) : (
                    <Field label="Day (e.g. Mondays)"><input className={inputCls} data-testid="f-day" value={form.day || ""} onChange={(e) => upd("day", e.target.value)} /></Field>
                  )}
                  <Field label="Time"><input className={inputCls} data-testid="f-time" value={form.time} onChange={(e) => upd("time", e.target.value)} /></Field>
                </div>
                <Field label="Location (pick a clinic)">
                  <ClinicOne clinics={clinics} value={form.clinic_id} onChange={(v) => { upd("clinic_id", v); const c = clinics.find((x) => x.id === v); if (c) upd("clinic", c.name); }} testid="f-sch-clinic" />
                </Field>
                <Field label="Location label (free text — overrides clinic name)"><input className={inputCls} data-testid="f-clinic" value={form.clinic} onChange={(e) => upd("clinic", e.target.value)} placeholder="e.g. Waddilove Clinic, Room 3" /></Field>
                <Field label="Description"><input className={inputCls} data-testid="f-description" value={form.description} onChange={(e) => upd("description", e.target.value)} /></Field>
              </>
            )}
            {modal.type === "events" && (
              <>
                <Field label="Title"><input className={inputCls} data-testid="f-title" value={form.title} onChange={(e) => upd("title", e.target.value)} /></Field>
                <Field label="Description"><textarea rows={3} className={inputCls} data-testid="f-description" value={form.description} onChange={(e) => upd("description", e.target.value)} /></Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Date"><input type="date" className={inputCls} data-testid="f-date" value={form.date} onChange={(e) => upd("date", e.target.value)} /></Field>
                  <Field label="Location"><input className={inputCls} data-testid="f-location" value={form.location} onChange={(e) => upd("location", e.target.value)} /></Field>
                </div>
                <Field label="Category">
                  <select className={inputCls} data-testid="f-category" value={form.category} onChange={(e) => upd("category", e.target.value)}>
                    {catsByKind("event").map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Target clinics">
                  <ClinicMulti clinics={clinics} value={form.clinic_ids || []} onChange={(v) => upd("clinic_ids", v)} />
                </Field>
                <Field label="Media (images / short videos / URLs)">
                  <MediaInput media={form.media || []} onChange={(m) => upd("media", m)} />
                </Field>
                <Field label="Voice note (optional)">
                  <VoiceRecorderInput value={form.audio_url || ""} onChange={(u) => upd("audio_url", u)} testid="event-voice" />
                </Field>

              </>
            )}
            <button onClick={save} disabled={busy || (modal.type === "announcements" && (!form.title?.trim() || !form.body?.trim()))} data-testid="save-btn" className="w-full bg-clinical text-white rounded-md py-2.5 font-semibold hover:bg-clinicaldark transition-colors disabled:opacity-60">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
